import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Users, TrendingUp, FileCheck, Database, CheckCircle2, Sprout, Truck, Award } from 'lucide-react';

const VerifiedDirectory = () => {
  const forBuyers = [
    'Faster discovery of credible sellers/exporters',
    'Credential vault and evidence-based onboarding',
    'Lane reliability scoring',
    'Reduced counterparty risk',
    'Access to verified trade references',
    'Performance history transparency'
  ];

  const forSellers = [
    'Access to verified buyers/importers/distributors',
    'Credential vault + performance record',
    'Repeat-lane growth (not one-off deals)',
    'Build reputation through verified trades',
    'Transparent performance tracking',
    'Priority matching for quality performers'
  ];

  const forFarmers = [
    'Direct access to premium export markets',
    'Better price realization for produce',
    'Training on export-quality standards',
    'Support for organic & GlobalGAP certification',
    'Timely payments and transparent pricing',
    'Cold chain and logistics support'
  ];

  const features = [
    {
      icon: <Shield className="w-12 h-12 text-[#263776]" />,
      title: 'KYC + Credentials Vault',
      description: 'Secure, permissioned storage of licenses, registrations, and banking details'
    },
    {
      icon: <FileCheck className="w-12 h-12 text-[#F47D20]" />,
      title: 'Trade References',
      description: 'Consent-based sharing of trade history and lane performance'
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-[#263776]" />,
      title: 'Performance Scoring',
      description: 'Track quality claims, timeliness, and documentation accuracy'
    },
    {
      icon: <Users className="w-12 h-12 text-[#F47D20]" />,
      title: 'Matchmaking',
      description: 'Connect validated buyers with validated sellers for repeat lanes'
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Verified Directory</h1>
            <p className="text-xl text-slate-700 mb-4">
              International Database of Verifiable Buyers & Sellers
            </p>
            <p className="text-lg text-slate-600">
              A permissioned directory to reduce fraud, onboarding time, and unreliable lanes—built for repeat trade relationships.
            </p>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">The Counterparty Problem</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-red-600">Buyers Face</h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Difficulty verifying seller credentials</li>
                  <li>• Risk of fraud and non-performance</li>
                  <li>• No visibility into supplier track record</li>
                  <li>• Long onboarding times</li>
                  <li>• Unreliable quality and delivery</li>
                </ul>
              </Card>
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-red-600">Sellers Face</h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Difficulty reaching verified buyers</li>
                  <li>• Payment risk and disputes</li>
                  <li>• One-off deals instead of repeat lanes</li>
                  <li>• No way to showcase track record</li>
                  <li>• Limited access to growth opportunities</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">What's Inside the Directory</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 hover:shadow-xl transition-shadow">
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-3 text-center">{feature.title}</h3>
                  <p className="text-slate-600 text-center">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* For Buyers */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <Database className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-4xl font-bold mb-6">For Buyers</h2>
                <p className="text-lg text-slate-700 mb-6">
                  Find credible exporters and suppliers faster, with verified credentials and transparent performance history.
                </p>
                <Button asChild size="lg">
                  <Link to="/verified-directory/apply-buyer">Apply as a Buyer</Link>
                </Button>
              </div>
              <div>
                <Card className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Buyer Benefits</h3>
                  <ul className="space-y-3">
                    {forBuyers.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-1" />
                        <span className="text-slate-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Sellers */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="md:order-2">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <TrendingUp className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-4xl font-bold mb-6">For Sellers</h2>
                <p className="text-lg text-slate-700 mb-6">
                  Build your reputation, access verified buyers, and grow repeat trade lanes with performance-based matching.
                </p>
                <Button asChild size="lg">
                  <Link to="/verified-directory/apply-seller">Apply as a Seller</Link>
                </Button>
              </div>
              <div className="md:order-1">
                <Card className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Seller Benefits</h3>
                  <ul className="space-y-3">
                    {forSellers.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-1" />
                        <span className="text-slate-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Farmers - NEW */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <Sprout className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-4xl font-bold mb-6 text-[#263776]">For Farmers</h2>
                <p className="text-lg text-slate-700 mb-4">
                  Join our <strong>ORIVANA Fresh Partner Network</strong> and get direct access to premium export markets for your fresh produce.
                </p>
                <p className="text-slate-600 mb-6">
                  We work directly with farmers across India for grapes, mangoes, pomegranates, citrus fruits, onions, and vegetables.
                </p>
                <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                  <Link to="/verified-directory/apply-farmer">
                    <Sprout className="w-4 h-4 mr-2" />
                    Become a Partner Farmer
                  </Link>
                </Button>
              </div>
              <div>
                <Card className="p-6 border-2 border-green-200">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-green-600" />
                    Farmer Benefits
                  </h3>
                  <ul className="space-y-3">
                    {forFarmers.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-1" />
                        <span className="text-slate-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">How It Works</h2>
            <div className="space-y-6">
              <Card className="p-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">1</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Apply & Verify</h3>
                    <p className="text-slate-600">Submit application with credentials (licenses, registrations, banking details)</p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">2</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">KYC Review</h3>
                    <p className="text-slate-600">Our team reviews and verifies credentials through multiple sources</p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">3</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Directory Listing</h3>
                    <p className="text-slate-600">Approved members are added to the permissioned directory with verified badge</p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">4</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Matchmaking & Trading</h3>
                    <p className="text-slate-600">Connect with verified counterparties and build repeat trade lanes</p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">5</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Performance Tracking</h3>
                    <p className="text-slate-600">Build your reputation through successful trades and positive performance scores</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join the Verified Directory</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Reduce risk, save time, and build repeatable trade lanes with verified counterparties.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/verified-directory/apply-buyer">Apply as Buyer</Link>
            </Button>
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/verified-directory/apply-seller">Apply as Seller</Link>
            </Button>
            <Button asChild size="lg" className="bg-green-600 text-white hover:bg-green-700">
              <Link to="/verified-directory/apply-farmer">
                <Sprout className="w-4 h-4 mr-2" />
                Partner Farmer
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VerifiedDirectory;
